import { SortOrder } from 'antd/es/table/interface';
import { ColumnType } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';

import { ENDPOINTS } from 'config/endpoints';
import { translateApiValue } from 'utils/translateApiValue';
import { FormListEntity } from 'entities/FormList';
import { normalizeSortParams } from 'entities/Sort/normalize';
import { FormStatus, TableParamsChangeConfig } from 'entities/common';
import {
  FormGetRequestApi,
  FormGetResponseApi,
} from 'entities/endpoints/form.get_form';
import {
  FormListRequest,
  FormListResponse,
} from 'entities/endpoints/form.list';
import {
  FormRemoveRequest,
  FormRemoveResponse,
} from 'entities/endpoints/form.remove';
import {
  GetNotificationRequest,
  GetNotificationResponse,
} from 'entities/endpoints/form.notification.get';
import {
  GetNotificationsRequest,
  GetNotificationsResponse,
  NotificationEntity,
} from 'entities/endpoints/form.notification.list';
import {
  NotificationRecipientType,
  SendNotificationRequest,
  SendNotificationResponse,
} from 'entities/endpoints/form.notification.send';
import {
  RecipientsCountRequest,
  RecipientsCountResponse,
} from 'entities/endpoints/form.notification.recipientsCount';
import { FormUpdateRequestApi } from 'entities/endpoints/form.update_form';
import { ApiRequest } from 'stores/common';
import { ILocalStore } from 'stores/interfaces';

import { normalizeFormListPage } from './normalize';

type PrivateFields = '_formList' | '_sort' | '_sortParams' | '_notifications' | '_recipientsCounts';

type SortParams = Pick<SorterResult<any>, 'column' | 'order'> | null;

/** Стор для списка форм */
class FormListPageStore implements ILocalStore {
  constructor() {
    makeObservable<FormListPageStore, PrivateFields>(this, {
      _formList: observable.ref,
      formList: computed,

      _sort: observable.ref,
      _sortParams: computed,
      sortOrder: computed,
      loading: computed,
      formListSuccess: computed,

      _notifications: observable,
      notifications: computed,

      _recipientsCounts: observable,
      recipientsCounts: computed,

      fetchData: action.bound,
      removeForm: action.bound,
      approveForm: action.bound,
      sendNotification: action.bound,
      fetchNotifications: action.bound,
      fetchRecipientsCount: action.bound,
    });
  }

  private _sort: SortParams = null;
  private _formList: FormListEntity[] = [];
  private _notifications: Record<number, NotificationEntity[]> = {};
  private _recipientsCounts: Record<number, RecipientsCountResponse> = {};

  private readonly _formListRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.formList,
    methodType: 'GET',
  });

  private readonly _formRemoveRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.remove,
    methodType: 'POST',
  });

  private readonly _formUpdateRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.update,
    methodType: 'POST',
  });

  private readonly _sendNotificationRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.notification.send,
    methodType: 'POST',
  });

  private readonly _getNotificationsRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.notification.list,
    methodType: 'GET',
  });

  private readonly _getFormRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.get,
    methodType: 'GET',
  });

  private readonly _recipientsCountRequest = new ApiRequest({
    endpoint: ENDPOINTS.form.notification.recipientsCount,
    methodType: 'GET',
  });

  private get _sortParams() {
    if (!this._sort || !this._sort.column || !this._sort.order) {
      return;
    }

    return normalizeSortParams(
      String(this._sort.column.dataIndex),
      this._sort.order
    );
  }

  get formList(): FormListEntity[] {
    return this._formList;
  }

  get formListSuccess() {
    return this._formListRequest.success;
  }

  get loading() {
    return (
      this._formRemoveRequest.loading ||
      this._formListRequest.loading ||
      this._formUpdateRequest.loading ||
      this._sendNotificationRequest.loading ||
      this._getNotificationsRequest.loading
    );
  }

  get notifications(): Record<number, NotificationEntity[]> {
    return this._notifications;
  }

  get recipientsCounts(): Record<number, RecipientsCountResponse> {
    return this._recipientsCounts;
  }

  get sortOrder(): SortOrder {
    return (this._sort && this._sort.order) || null;
  }

  /**
   * @param tableParams - Массив изменившихся параметров таблицы ( сортировка, фильтрация и т.п )
   * */
  async handleTableParamsChange({
    sort,
  }: TableParamsChangeConfig): Promise<void> {
    let order: SortOrder | undefined;

    let column: ColumnType<any> | undefined;

    if (sort instanceof Array) {
      ({ order, column } = sort[0]);
    } else {
      column = sort.column;
      order = sort.order;
    }

    runInAction(() => {
      this._sort = { order, column };
    });

    await this.fetchData();
  }

  async fetchData(params: FormListRequest = {}) {
    const { data, isError } = await this._formListRequest.call<
      FormListResponse,
      FormListRequest
    >({
      data: {
        sort: this._sortParams,
        ...params,
      },
    });

    if (!isError && data) {
      runInAction(() => {
        this._formList = normalizeFormListPage(data);
      });
    }
  }

  async removeForm(id: number) {
    const { isError } = await this._formRemoveRequest.call<
      FormRemoveResponse,
      FormRemoveRequest
    >({
      data: { id },
    });

    if (isError) {
      return;
    }

    await this.fetchData();
  }

  async approveForm(id: number) {
    const { isError } = await this._formUpdateRequest.call<
      FormGetResponseApi,
      FormUpdateRequestApi
    >({
      data: {
        id,
        status: FormStatus.FINISHED,
      },
    });

    if (isError) {
      return;
    }

    await this.fetchData();
  }

  async sendNotification(
    formId: number,
    recipientType: NotificationRecipientType,
    subject: string,
    message: string,
    targetEmail?: string,
  ) {
    const requestData: SendNotificationRequest = {
      id: formId,
      recipient_type: recipientType,
      subject,
      message,
    };

    if (targetEmail) {
      requestData.target_email = targetEmail;
    }

    const { data, isError } = await this._sendNotificationRequest.call<
      SendNotificationResponse,
      SendNotificationRequest
    >({
      data: requestData,
    });

    if (!isError && data) {
      // Обновляем список уведомлений после отправки
      await this.fetchNotifications(formId);
    }

    return { isError, data };
  }

  async fetchNotifications(formId: number) {
    const { data, isError } = await this._getNotificationsRequest.call<
      GetNotificationsResponse,
      GetNotificationsRequest
    >({
      data: {
        id: formId,
      },
    });

    if (!isError && data) {
      runInAction(() => {
        // Создаем новый объект, чтобы MobX отследил изменение
        this._notifications = {
          ...this._notifications,
          [formId]: data.notifications,
        };
      });
    }

    return { isError, data };
  }

  async fetchRecipientsCount(formId: number) {
    const { data, isError } = await this._recipientsCountRequest.call<
      RecipientsCountResponse,
      RecipientsCountRequest
    >({
      data: { id: formId },
    });

    if (!isError && data) {
      runInAction(() => {
        this._recipientsCounts = {
          ...this._recipientsCounts,
          [formId]: data,
        };
      });
    }
  }

  /**
   * Получить список полей (столбцов) формы для уведомлений
   */
  async fetchFormColumns(formId: number): Promise<{ id: string; name: string }[]> {
    const { data, isError } = await this._getFormRequest.call<
      FormGetResponseApi,
      FormGetRequestApi
    >({
      data: {
        id: formId,
        with_fields: true,
      },
    });

    if (isError || !data || !data.customizable_fields) {
      return [];
    }

    return data.customizable_fields.map((field) => ({
      id: field.id,
      name: translateApiValue(field.field_data.name),
    }));
  }

  destroy() {
    this._formListRequest.destroy();
    this._formRemoveRequest.destroy();
    this._formUpdateRequest.destroy();
    this._sendNotificationRequest.destroy();
    this._getNotificationsRequest.destroy();
    this._getFormRequest.destroy();
    this._recipientsCountRequest.destroy();
  }
}

export default FormListPageStore;
